/* eslint-disable react-doctor/no-adjust-state-on-prop-change, react-hooks-js/set-state-in-effect */
import { useState, useSyncExternalStore } from "react";
import { addPageScrollListener } from "./app-scroll.ts";

const SENTINEL_HYSTERESIS_PX = 8;

export function useSentinelPastViewportEdge(
    sentinel: HTMLElement | null,
    edge: "top" | "bottom",
    {
        enabled = true,
        offsetPx = 0,
        sentinelEdge = "top",
    }: {
        enabled?: boolean;
        offsetPx?: number;
        sentinelEdge?: "top" | "bottom";
    } = {},
): boolean {
    const [store] = useState(() => ({
        edge: null as "top" | "bottom" | null,
        sentinel: null as HTMLElement | null,
        pastEdge: false,
    }));
    return useSyncExternalStore(
        (listener) => {
            if (!enabled || !sentinel) {
                store.edge = null;
                store.sentinel = null;
                store.pastEdge = false;
                return () => {};
            }
            let frame: number | null = null;

            if (store.sentinel !== sentinel || store.edge !== edge) {
                store.edge = edge;
                store.sentinel = sentinel;
                store.pastEdge = false;
            }

            const update = () => {
                const rect = sentinel.getBoundingClientRect();
                const y = sentinelEdge === "top" ? rect.top : rect.bottom;
                const edgeY =
                    edge === "top" ? 0 : window.innerHeight - offsetPx;
                const next = store.pastEdge
                    ? y < edgeY + SENTINEL_HYSTERESIS_PX
                    : y <= edgeY - SENTINEL_HYSTERESIS_PX;
                if (next === store.pastEdge) return;
                store.pastEdge = next;
                listener();
            };

            const scheduleUpdate = () => {
                if (frame !== null) return;
                frame = window.requestAnimationFrame(() => {
                    frame = null;
                    update();
                });
            };

            update();
            const unsubscribe = addPageScrollListener(scheduleUpdate);

            return () => {
                if (frame !== null) {
                    window.cancelAnimationFrame(frame);
                }
                unsubscribe();
            };
        },
        () =>
            enabled &&
            sentinel &&
            store.sentinel === sentinel &&
            store.edge === edge
                ? store.pastEdge
                : false,
        () => false,
    );
}
